import React, {
  useImperativeHandle,
  useState,
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
} from 'react';
import {
  Menu as MuiMenu,
  MenuItem,
  Divider,
  Typography,
  ListSubheader,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import './ArrMenu.scss';

import {ReactComponent as TickIcon} from '../../../assets/images/tick-icon.svg';
import {MoreHoriz} from '@mui/icons-material';
import ArrTooltip from '../arr-tooltip/ArrTooltip';
import useArrTabFocus from '../arr-tab-focus/useArrTabFocus';

export type CustomMenuItem = {
  label: string | ReactElement;
  href?: string;
  value?: string;
  id: string;
  disabled?: boolean;
  submenu?: CustomMenuItem[];
  checked?: boolean;
  selected?: boolean;
  onToggle?: () => void;
  onClick?: (selectedItem?: string) => void;
  variant?: 'default' | 'danger' | 'heading' | 'divider';
};

export type ArrMenuHandle = {
  open: (anchorEl: HTMLElement, autoOpen?: boolean) => void;
  close: () => void;
  toggle: (anchorEl: HTMLElement) => void;
};

export type Origin = {
  vertical: 'top' | 'bottom' | 'center' | number;
  horizontal: 'left' | 'right' | 'center' | number;
};

type MenuProps = {
  items: CustomMenuItem[];
  title?: string;
  onItemClick?: (item: CustomMenuItem) => void;
  selectedCustomMenuItem?: CustomMenuItem;
  onClose?: () => void;
  id: string;
  className?: string;
  direction?:
    | 'bottom'
    | 'top'
    | 'left'
    | 'right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-left'
    | 'top-right';
  subMenuDirection?: 'left' | 'right';
  anchorOrigin?: Origin;
  transformOrigin?: Origin;
  renderItem?: (
    item: CustomMenuItem,
    helpers: {close: () => void}
  ) => ReactElement | null;
  keepOpenOnItemClick?: boolean;
  autoFocusMenu?: boolean;
  disableAutoFocusItem?: boolean;
  /**
   * When true with autoFocusMenu and disableAutoFocusItem=false, focuses the first
   * [role=menuitem] after open (helps when Modal/DataGrid steals MUI autoFocus).
   */
  focusFirstMenuItemOnOpen?: boolean;
  enableTooltip?: boolean;
  variant?: 'standard' | 'condensed';
  tabGroup?: string;
};

const getOriginsFromDirection = (dir?: MenuProps['direction']) => {
  switch (dir) {
    case 'bottom-left':
      return {
        anchorOrigin: {vertical: 'bottom', horizontal: 'left'} as Origin,
        transformOrigin: {vertical: 'top', horizontal: 'left'} as Origin,
      };
    case 'bottom-right':
      return {
        anchorOrigin: {vertical: 'bottom', horizontal: 'right'} as Origin,
        transformOrigin: {vertical: 'top', horizontal: 'right'} as Origin,
      };
    case 'top-left':
      return {
        anchorOrigin: {vertical: 'top', horizontal: 'left'} as Origin,
        transformOrigin: {vertical: 'bottom', horizontal: 'left'} as Origin,
      };
    case 'top-right':
    case 'top':
      return {
        anchorOrigin: {vertical: 'top', horizontal: 'right'} as Origin,
        transformOrigin: {vertical: 'bottom', horizontal: 'right'} as Origin,
      };
    case 'right':
      return {
        anchorOrigin: {vertical: 'top', horizontal: 'right'} as Origin,
        transformOrigin: {vertical: 'top', horizontal: 'left'} as Origin,
      };
    case 'left':
      return {
        anchorOrigin: {vertical: 'center', horizontal: 'left'} as Origin,
        transformOrigin: {vertical: 'center', horizontal: 'right'} as Origin,
      };
    case 'bottom':
    default:
      return {
        anchorOrigin: {vertical: 'bottom', horizontal: 'center'} as Origin,
        transformOrigin: {vertical: 'top', horizontal: 'center'} as Origin,
      };
  }
};

const ArrMenu = forwardRef<ArrMenuHandle, MenuProps>(
  (
    {
      items,
      title,
      onItemClick,
      onClose,
      id = 'arr-menu',
      className,
      direction = 'right',
      subMenuDirection = 'right',
      anchorOrigin,
      transformOrigin,
      selectedCustomMenuItem,
      renderItem,
      keepOpenOnItemClick = false,
      autoFocusMenu = false,
      disableAutoFocusItem = true,
      focusFirstMenuItemOnOpen = true,
      enableTooltip = false,
      variant = 'standard',
      tabGroup,
    },
    ref
  ) => {
    const {setModalOpen: setArrTabFocusModal} = useArrTabFocus();
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [autoOpen, setAutoOpen] = useState<boolean>(true);
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(
      null
    );
    const [submenuItems, setSubmenuItems] = useState<CustomMenuItem[]>([]);
    const [submenuId, setSubmenuId] = useState<string>('');
    const [activeSubmenuParentId, setActiveSubmenuParentId] = useState<
      string | null
    >(null);
    const open = Boolean(anchorEl);
    const submenuOpen = Boolean(submenuAnchorEl);

    const closeEntireMenu = useCallback(() => {
      setAnchorEl(null);
      setSubmenuAnchorEl(null);
      setActiveSubmenuParentId(null);
      setArrTabFocusModal?.(false);
      onClose?.();
    }, [onClose]);

    useEffect(() => {
      if (
        !open ||
        submenuOpen ||
        !autoFocusMenu ||
        disableAutoFocusItem ||
        !focusFirstMenuItemOnOpen
      ) {
        return;
      }
      let cancelled = false;
      const focusFirst = () => {
        if (cancelled) return;
        const root = document.getElementById(id);
        if (!root) return;
        const first = root.querySelector(
          '[role="menuitem"]:not([aria-disabled="true"])'
        ) as HTMLElement | null;
        first?.focus({preventScroll: true});
      };
      const t0 = window.setTimeout(focusFirst, 0);
      const t1 = window.setTimeout(focusFirst, 100);
      return () => {
        cancelled = true;
        window.clearTimeout(t0);
        window.clearTimeout(t1);
      };
    }, [
      open,
      submenuOpen,
      autoFocusMenu,
      disableAutoFocusItem,
      focusFirstMenuItemOnOpen,
      id,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        open: (anchor: HTMLElement, autoOpen: boolean = true) => {
          setAnchorEl(anchor);
          setAutoOpen(autoOpen);
          setArrTabFocusModal?.(true);
        },
        close: closeEntireMenu,
        toggle: (anchor: HTMLElement) => {
          setAnchorEl((prev) => (prev ? null : anchor));
          setArrTabFocusModal?.((prev: boolean) => !prev);
        },
      }),
      [closeEntireMenu]
    );

    const handleSubmenuClose = useCallback(() => {
      setSubmenuAnchorEl(null);
      setSubmenuItems([]);
      setSubmenuId('');
      setActiveSubmenuParentId(null);
    }, []);

    const handleMenuClose = useCallback(
      (_event?: unknown, _reason?: string) => {
        closeEntireMenu();
      },
      [closeEntireMenu]
    );

    const handleItemClick = (item: CustomMenuItem) => {
      return (_event: React.MouseEvent<HTMLElement>) => {
        if (item.submenu && item.submenu.length > 0) {
          _event.stopPropagation();
          setSubmenuAnchorEl(_event.currentTarget);
          setSubmenuItems(item.submenu);
          setSubmenuId(`${id}-submenu-${item.id}`);
          setActiveSubmenuParentId(item.id);
          return;
        }
        onItemClick?.(item);
        if (!keepOpenOnItemClick) {
          closeEntireMenu();
        }
      };
    };

    const computed = getOriginsFromDirection(direction);
    const finalAnchorOrigin = anchorOrigin ?? computed.anchorOrigin;
    const finalTransformOrigin = transformOrigin ?? computed.transformOrigin;
    if (!items || items.length === 0) {
      return null;
    }
    return (
      <>
        <MuiMenu
          id={id}
          className={`arr-menu ${className ?? ''} ${autoOpen ? '' : 'inactive'} ${variant === 'condensed' ? 'arr-menu--condensed' : ''}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={finalAnchorOrigin}
          transformOrigin={finalTransformOrigin}
          disableAutoFocusItem={disableAutoFocusItem}
          data-testid="arr-menu-parent"
          disableScrollLock
          autoFocus={autoFocusMenu}
          slotProps={{
            list: {
              disablePadding: true,
              dense: true,
              'data-testid': 'arr-action-menu-list',
            },
          }}
          disableRestoreFocus
        >
          {title && (
            <ListSubheader
              sx={{backgroundColor: 'transparent'}}
              tabIndex={-1}
              className="arr-menu__header"
            >
              <Typography
                className="typo-text label2_size arr-menu__header-text"
                data-testid={`arr-menu-title-${id}`}
              >
                {title}
              </Typography>
            </ListSubheader>
          )}
          {title && <Divider className="arr-menu__divider" />}

          {items.map((item, index) => {
            if (renderItem) {
              const rendered = renderItem(item, {
                close: closeEntireMenu,
              });

              if (!rendered) return null;

              return React.isValidElement(rendered)
                ? React.cloneElement(rendered, {key: item.id} as never)
                : rendered;
            }

            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isSubmenuActive = activeSubmenuParentId === item.id;
            const variantClass =
              item.variant === 'danger'
                ? 'arr-menu__item--danger'
                : item.variant === 'heading'
                  ? 'arr-menu__item--heading'
                  : '';

            const LABEL_TO_TEST_ID: Record<string, string> = {
              Summarize: 'summarize',
              Investigate: 'investigate',
              Review: 'review',
            };

            const getDataTestId = (item: any, prefix: string) => {
              const mapped = LABEL_TO_TEST_ID[item.label];
              return mapped
                ? `nav-submenu-${mapped}`
                : `${prefix}-${item.id}-item`;
            };

            return (
              <MenuItem
                key={item.id}
                onClick={handleItemClick(item)}
                data-testid={getDataTestId(item, id)}
                className={`arr-menu__item ${
                  hasSubmenu ? 'arr-menu__item--has-submenu' : ''
                } ${item.selected ? 'arr-menu__item--is-selected' : ''} ${
                  isSubmenuActive ? 'arr-menu__item--submenu-active' : ''
                } ${variantClass}`}
                selected={selectedCustomMenuItem?.label === item.label}
                disabled={item.disabled}
                disableRipple
                component={item.href ? 'a' : 'div'}
                href={item.href}
                role="menuitem"
              >
                <span
                  className="arr-menu__item-content"
                  data-testid={`arr-menu-item-${id}-${index}`}
                  data-tab-id={`arr-menu-item-${id}-${index}`}
                  data-tab-group={tabGroup}
                >
                  {(() => {
                    const labelContent =
                      typeof item.label === 'string'
                        ? t(item.label as unknown as string)
                        : item.label;
                    if (
                      enableTooltip &&
                      typeof labelContent === 'string' &&
                      labelContent.length > 20
                    ) {
                      return (
                        <ArrTooltip
                          content={labelContent}
                          isTruncated={true}
                          showOnTruncate={true}
                          labelId={`tooltip-menu-${index}`}
                        >
                          {labelContent}
                        </ArrTooltip>
                      );
                    }
                    return labelContent;
                  })()}
                </span>
                {hasSubmenu && (
                  <div
                    className="arr-menu__item-dots"
                    data-testid={`arr-menu-more-${index}`}
                  >
                    <MoreHoriz />
                  </div>
                )}
                {item.selected && (
                  <TickIcon data-test-id="arr-menu-item-selected-icon" />
                )}
              </MenuItem>
            );
          })}
        </MuiMenu>
        {submenuOpen && (
          <MuiMenu
            id={submenuId}
            className={`arr-menu arr-menu--submenu ${subMenuDirection === 'right' ? 'arr-menu--right-submenu' : 'arr-menu--left-submenu'}  ${className ?? ''}`}
            anchorEl={submenuAnchorEl}
            open={submenuOpen}
            onClose={handleSubmenuClose}
            anchorOrigin={{vertical: 'top', horizontal: subMenuDirection}}
            transformOrigin={{
              vertical: 'top',
              horizontal: subMenuDirection == 'right' ? 'left' : 'right',
            }}
            slotProps={{
              list: {
                disablePadding: true,
                dense: true,
                autoFocusItem: true,
                'data-testid': 'arr-action-sub-menu-list',
              },
            }}
            role="menu"
          >
            {submenuItems.map((item, index) => {
              if (renderItem) {
                const customItemClassName =
                  item.variant === 'divider'
                    ? 'arr-menu__custom-divider'
                    : 'arr-menu__custom-item';
                return (
                  <div
                    key={item.id}
                    className={`arr-menu__item ${customItemClassName}`}
                    data-testid={`arr-menu-submenu-${index}`}
                  >
                    {renderItem(item, {
                      close: closeEntireMenu,
                    })}
                  </div>
                );
              }

              return (
                <MenuItem
                  key={item.id}
                  onClick={handleItemClick(item)}
                  data-testid={`${submenuId}-${item.id}-submenu-item`}
                  className={`arr-menu__item ${
                    item.variant === 'danger'
                      ? 'arr-menu__item--danger'
                      : item.variant === 'heading'
                        ? 'arr-menu__item--heading'
                        : ''
                  }`}
                  selected={selectedCustomMenuItem?.label === item.label}
                  disabled={item.disabled}
                  disableRipple
                  component={item.href ? 'a' : 'div'}
                  href={item.href}
                >
                  <span
                    className="arr-menu__item-content"
                    data-testid={`arr-menu-submenu-label-${index}`}
                  >
                    {(() => {
                      const labelContent =
                        typeof item.label === 'string'
                          ? t(item.label as unknown as string)
                          : item.label;
                      if (
                        enableTooltip &&
                        typeof labelContent === 'string' &&
                        labelContent.length > 20
                      ) {
                        return (
                          <ArrTooltip
                            content={labelContent}
                            isTruncated={true}
                            showOnTruncate={true}
                            labelId={`tooltip-submenu-${index}`}
                          >
                            {labelContent}
                          </ArrTooltip>
                        );
                      }
                      return labelContent;
                    })()}
                  </span>
                </MenuItem>
              );
            })}
          </MuiMenu>
        )}
      </>
    );
  }
);

export default ArrMenu;
